import React, { useEffect, useMemo, useState } from 'react';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CustomSelect from 'components/Form/CustomSelect';
import { BondType } from '../types/on-boarding.type';
import Button from 'components/Form/Button';
import {
  useCreateBond,
  useDeleteBond,
  useGetBondList
} from 'common/queries-and-mutations/bond';
import { Loader } from 'components/Loader';
import { findNextPage, findPreviousPage, showRemoveButton } from 'utils';
import { countriesCurrency } from 'constants/countries-currency';
import InfoBar from './InfoBar';
import { STEPS } from 'constants/steps';
import { fetchUserInformation } from 'thunks/account-thunk';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';
import { useSkipOnboardingStep } from 'common/queries-and-mutations/user';

export default function BondInfo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isError, isLoading } = useCreateBond();
  const { mutate: deleteBond, isLoading: isDeleting } = useDeleteBond();
  const { mutate: skipOnboarding, isSuccess: skipSuccess } =
    useSkipOnboardingStep();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const planAllowPage = user && JSON.parse(user?.planAllowPage);

  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const {
    data,
    isSuccess: isBondSuccess,
    isLoading: isBondLoading
  } = useGetBondList();

  const bonds = useMemo(() => {
    if (isBondSuccess && data.length > 0) {
      return data.map(bond => ({
        bondId: bond.id,
        issuerName: bond.issuerName,
        description: bond.description,
        coupon: bond.coupon,
        maturityDate: bond.maturityDate,
        currency: bond.currency,
        faceValue: bond.faceValue,
        ISINNo: bond.ISINNo,
        unit: bond.unit
      }));
    } else {
      return [
        {
          bondId: null,
          issuerName: '',
          description: '',
          coupon: '',
          maturityDate: '',
          currency: '',
          faceValue: '',
          ISINNo: '',
          unit: ''
        }
      ];
    }
  }, [data, isBondSuccess]);

  const formik = useFormik({
    initialValues: {
      bonds
    },
    validationSchema: Yup.object({
      bonds: Yup.array().of(
        Yup.object().shape({
          issuerName: Yup.string()
            .required('Issuer name is required')
            .nullable(),
          description: Yup.string()
            .required('Description is required')
            .nullable(),
          // coupon: Yup.string().required('Coupon is required').nullable(),
          // maturityDate: Yup.date().required('Maturity Date is required').nullable(),
          currency: Yup.string().required('Currency is required').nullable()
          // faceValue: Yup.string().required('Face value is required').nullable(),
          // ISINNo: Yup.string().required('ISIN No is required').nullable(),
          // unit: Yup.string().required('Unit is required').nullable()
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onBondSubmit(values);
    }
  });

  const addMoreBond = () => {
    formik.values.bonds.push({
      bondId: null,
      issuerName: '',
      description: '',
      coupon: '',
      maturityDate: '',
      currency: '',
      faceValue: '',
      ISINNo: '',
      unit: ''
    });
    formik.setFieldValue('bonds', [...formik.values.bonds]);
  };

  const removeBond = (indexToRemove: number, bondId?: number | null) => {
    if (bondId) {
      deleteBond(bondId);
    }
    formik.values.bonds.splice(indexToRemove, 1);
    formik.setFieldValue('bonds', [...formik.values.bonds]);
  };

  const onBondSubmit = (formValue: BondType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      dispatch(fetchUserInformation());
      setIsSubmitting(false);
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.BOND_INFORMATION));
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, dispatch, navigate, planAllowPage]);

  const onContinueLater = () => {
    if (Object.keys(formik.errors).length) {
      return;
    }
    mutate(formik.values);
  };

  const onPrevious = () => {
    navigate(findPreviousPage(planAllowPage, STEPS.BOND_INFORMATION));
  };

  const handleSkip = () => {
    setIsSubmitting(true);
    skipOnboarding();
  };

  useEffect(() => {
    if (skipSuccess) {
      dispatch(fetchUserInformation());
      navigate('/dashboard');
    }
  }, [skipSuccess, dispatch]);

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={2}
        stepPercentage={STEPS.BOND_INFORMATION}
        timelineStep={2}
      />
      {isBondLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <div className="flex w-full items-end justify-between">
            <span></span>
            <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
              Financial Asset - Bonds
            </p>
            <button
              type="button"
              className="mt-5 text-sm font-normal text-[#353535] underline"
              onClick={handleSkip}
            >
              Skip to Dashboard
            </button>
          </div>

          <form
            autoComplete="false"
            className="m-10"
            onSubmit={formik.handleSubmit}
          >
            {formik.values.bonds.map((bond, index) => (
              <div key={index} className="my-8">
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`bonds[${index}].issuerName`}
                      placeholder=""
                      value={bond.issuerName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Name of Issuer"
                      error={
                        formik.touched.bonds
                          ? formik.errors.bonds?.[index] &&
                            formik.errors.bonds.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.bonds[index])
                              ).issuerName
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`bonds[${index}].description`}
                      placeholder=""
                      value={bond.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Description"
                      error={
                        formik.touched.bonds
                          ? formik.errors.bonds?.[index] &&
                            formik.errors.bonds.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.bonds[index])
                              ).description
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`bonds[${index}].coupon`}
                      placeholder=""
                      value={bond.coupon}
                      onChange={e => {
                        if (/^[0-9]*$/.test(e.target.value)) {
                          formik.handleChange(e);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      label="Coupon/Interest Rate"
                      error={
                        formik.touched.bonds
                          ? formik.errors.bonds?.[index] &&
                            formik.errors.bonds.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.bonds[index])
                              ).coupon
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      type="date"
                      name={`bonds[${index}].maturityDate`}
                      placeholder=""
                      value={bond.maturityDate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Maturity Date (DD/MM/YYYY)"
                      error={
                        formik.touched.bonds
                          ? formik.errors.bonds?.[index] &&
                            formik.errors.bonds.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.bonds[index])
                              ).maturityDate
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <div className="group relative z-0 mb-6 w-full">
                      <CustomSelect
                        options={countriesCurrency.map(country => ({
                          value: country.currency,
                          label: `${country.name} (${country.currency})`
                        }))}
                        selectedOption={{
                          value: bond.currency,
                          label: bond.currency
                        }}
                        handleOptionChange={option => {
                          formik.setFieldValue(
                            `bonds[${index}].currency`,
                            option?.value
                          );
                        }}
                        label="Currency"
                        name="currency"
                        isRequired
                        isSearchable
                        className="h-[3.5rem] rounded-2xl"
                        error={
                          formik.touched.bonds
                            ? formik.errors.bonds?.[index] &&
                              formik.errors.bonds.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.bonds[index])
                                ).currency
                              : ''
                            : ''
                        }
                      />
                    </div>
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`bonds[${index}].faceValue`}
                      placeholder=""
                      value={bond.faceValue}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Face Value"
                      error={
                        formik.touched.bonds
                          ? formik.errors.bonds?.[index] &&
                            formik.errors.bonds.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.bonds[index])
                              ).faceValue
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="-mt-5 grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`bonds[${index}].ISINNo`}
                      placeholder=""
                      value={bond.ISINNo}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="ISIN No."
                      error={
                        formik.touched.bonds
                          ? formik.errors.bonds?.[index] &&
                            formik.errors.bonds.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.bonds[index])
                              ).ISINNo
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`bonds[${index}].unit`}
                      placeholder=""
                      value={bond.unit}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="How Many Units"
                      error={
                        formik.touched.bonds
                          ? formik.errors.bonds?.[index] &&
                            formik.errors.bonds.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.bonds[index])
                              ).unit
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                {showRemoveButton(index, bond.bondId, data!.length > 1) && (
                  <div className="group relative z-0 mt-1 w-full">
                    {isDeleting ? (
                      <Loader height={20} width={20} type={'Wait'} />
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeBond(index, bond.bondId)}
                      >
                        <Icon name={Icons.Delete} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Icon name={Icons.Required} />
                <span className="text-[10px] text-[#7A7C87]">
                  Indicates Required
                </span>
              </div>
              <div
                className="flex items-center gap-4"
                onClick={addMoreBond}
                role="button"
                tabIndex={-1}
              >
                <Icon name={Icons.AddCircleOutline} />
                <span className="mt-2 text-base font-medium text-[#353535]">
                  Add Another Account
                </span>
              </div>
            </div>

            <div className="flex w-full items-end justify-between">
              <Button
                variant="outline"
                size="md"
                className="mt-5 w-[9rem] rounded p-[0.1rem] text-primary-900"
                label="Previous"
                type="button"
                onClick={() => onPrevious()}
              ></Button>
              <div className="mt-10 flex items-end justify-end gap-8">
                {parentPath !== 'dashboard' && (
                  <button
                    type="submit"
                    className="font-circular text-[15px] font-bold text-[#353535] underline"
                    onClick={() => {
                      onContinueLater();
                      setIsLater(true);
                    }}
                  >
                    Save and Continue Later
                  </button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Proceed"
                  type="submit"
                  disabled={!formik.isValid}
                  loading={isSubmitting || isLoading}
                ></Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
